// Viewer Help Center content (HelpCenter.js). Answers describe what the
// backend and this site actually do, so update them when that changes. Each
// answer is a list of paragraphs; a nested array renders as a bullet list.
//
// `topics` lists the Contact Support categories (backend
// VIEWER_SUPPORT_CATEGORIES keys) an answer helps with; ContactSupport shows
// those answers as soon as the viewer picks that category.
//
// Deliberately NOT documented: the /devices and /link pages (the device list
// is sample data and /link's backend route doesn't exist) — TV sign-in is the
// /activate flow below.

export const HELP_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting started",
    icon: "start",
    items: [
      {
        q: "How do I create an account?",
        a: [
          "Choose Sign up, then enter your name, email and a password (at least 6 characters).",
          "We email you a 6-digit code to confirm your address. Enter it to finish signing up — you can't sign in until your email is confirmed. The code expires after a few minutes; use Resend to get a new one.",
        ],
      },
      {
        q: "How do I sign in on my TV?",
        topics: ["activation"],
        a: [
          [
            "Open the app on your TV. It shows a QR code and a 6-character code.",
            "Scan the QR code with your phone, or go to the activation page on this website.",
            "Sign in if you're asked to, then enter the code exactly as shown on the TV and choose Submit.",
            "Your TV signs in by itself a moment later.",
          ],
          "Codes only last a few minutes. If you see “Activation code has expired” or “Invalid Activation code”, open the sign-in screen on your TV again to get a new code.",
        ],
      },
      {
        q: "Where can I watch?",
        a: [
          "On this website in any modern browser, and in our apps for Roku, Amazon Fire TV and Android (Google Play). The store links are at the bottom of every page under “Available on”.",
          "Sign in with the same account everywhere: your subscription, purchases, profiles and lists come with you.",
        ],
      },
    ],
  },
  {
    id: "account",
    title: "Account & sign-in",
    icon: "account",
    items: [
      {
        q: "I forgot my password.",
        topics: ["login"],
        a: [
          "On the sign-in page choose Forgot password and enter your email. We send you a one-time code — enter it quickly, as it expires after about a minute (use Resend if it runs out). Then choose your new password.",
        ],
      },
      {
        q: "It says I need to verify my email.",
        topics: ["login"],
        a: [
          "Your account isn't confirmed yet. When you try to sign in we send a new 6-digit code to your email: enter it on the verification screen. Check your spam or promotions folder if it doesn't arrive within a few minutes.",
        ],
      },
      {
        q: "Why was I signed out?",
        topics: ["login"],
        a: [
          "For security, sign-ins expire after a while and the site renews them in the background. If that renewal fails (for example after a long time away, or if your password was changed), you're signed out and just need to sign in again.",
        ],
      },
      {
        q: "How do I change my password?",
        topics: ["account"],
        a: [
          "Go to Settings → Change Password. You'll need your current password; the new one must be at least 6 characters.",
        ],
      },
      {
        q: "How many profiles can I have?",
        topics: ["account"],
        a: [
          "Up to 5 profiles per account, each with its own name and avatar. Manage them from the profile screen or Settings → Switch Profile. Profile names must be different from each other.",
        ],
      },
      {
        q: "How do I stop new-release emails?",
        a: [
          "Use the Unsubscribe link at the bottom of any new-release email. Emails about your account, like sign-in codes and receipts, still arrive.",
        ],
      },
      {
        q: "How do I delete my account?",
        topics: ["account"],
        a: [
          "Send us a request through Contact Support from the email on your account and we'll take care of it.",
        ],
      },
    ],
  },
  {
    id: "billing",
    title: "Subscriptions & billing",
    icon: "billing",
    items: [
      {
        q: "Is there a free trial?",
        a: [
          "Some plans include a free trial; the plan shows “Start Free Trial” when you're eligible. Trials are one per person: if you've had a trial or a paid subscription before, on any device, you'll see Subscribe instead.",
          "Cancel before the trial ends and you won't be charged.",
        ],
      },
      {
        q: "How do I cancel my subscription?",
        topics: ["subscription"],
        a: [
          "If you subscribed on this website: go to your Subscription page, choose Manage your subscription → Cancel Subscription, and confirm.",
          "You keep watching until the end of the period you've paid for, then your account moves to the free plan. Changed your mind? Choose Resume Subscription before the period ends.",
        ],
      },
      {
        q: "I subscribed on Roku, Google Play, Apple or Amazon.",
        topics: ["subscription"],
        a: [
          "Those subscriptions are billed by that store, so they can only be changed or cancelled there (for example in your Roku account or the Google Play Store's Subscriptions page). They don't appear as cancellable on this website.",
        ],
      },
      {
        q: "How do I update my card or get an invoice?",
        topics: ["billing"],
        a: [
          "On your Subscription page choose Manage payment method & invoices. It opens our secure payment page where you can change your card and download past invoices.",
        ],
      },
      {
        q: "How does upgrading or downgrading work?",
        topics: ["subscription"],
        a: [
          [
            "Upgrade: takes effect straight away. You pay the difference for the rest of the current period; your renewal date doesn't change.",
            "Downgrade: takes effect at your next renewal, with nothing to pay today.",
          ],
          "You'll see the exact amount before you confirm.",
        ],
      },
      {
        q: "My payment failed.",
        topics: ["billing"],
        a: [
          "We retry the card automatically and you keep access while we do. Update your card from Manage payment method & invoices to fix it sooner. If the payment still can't be collected, the subscription ends.",
        ],
      },
      {
        q: "I was charged and something looks wrong.",
        topics: ["billing"],
        a: [
          "Send us a request through Contact Support with the date and amount (a screenshot of the receipt helps) and we'll look into it.",
        ],
      },
    ],
  },
  {
    id: "watching",
    title: "Watching",
    icon: "play",
    items: [
      {
        q: "The video keeps buffering or won't start.",
        topics: ["playback", "av_quality"],
        a: [
          [
            "Check your internet connection, and pause other heavy downloads or streams.",
            "Restart the app or refresh the page, and restart your router if other apps are slow too.",
            "Picture quality adjusts automatically to your connection, so a slower connection lowers quality before it stops playback.",
          ],
          "Still happening? Contact Support and tell us the title, your device and roughly what time it happened.",
        ],
      },
      {
        q: "It says “Device limit reached”.",
        topics: ["playback"],
        a: [
          "Your account is already playing on the maximum number of screens at once. Stop playback on another device and try again. A device that was closed without stopping is released automatically after about two minutes.",
        ],
      },
      {
        q: "How do I turn subtitles or another language on?",
        topics: ["av_quality"],
        a: [
          "While a video plays, choose the CC (Subtitles) button and pick a language, or Off. If the title has other audio languages, an audio button appears next to it. Neither button shows if the title has no subtitles or extra audio.",
        ],
      },
      {
        q: "Why can't I watch a title?",
        topics: ["playback", "content"],
        a: [
          [
            "“Subscribe to Watch”: the title needs a subscription.",
            "“Please buy/rent the content to watch”: it's available to buy or rent.",
            "Not there at all: it may not be released yet. Use Search to check the exact title.",
          ],
        ],
      },
      {
        q: "Where are Continue Watching and My List?",
        topics: ["content"],
        a: [
          "Under Account → My Library, along with your Favorites. They belong to the profile you're using, so switch profiles if something's missing.",
        ],
      },
    ],
  },
  {
    id: "apps",
    title: "Apps & devices",
    icon: "device",
    items: [
      {
        q: "The app crashes or won't open.",
        topics: ["app"],
        a: [
          [
            "Update the app to the latest version from your device's app store.",
            "Restart your device (unplug a TV or streaming stick for 30 seconds).",
            "If it keeps happening, uninstall and reinstall the app, then sign in again.",
          ],
          "Contact Support with your device model if none of that helps.",
        ],
      },
      {
        q: "I can't find the app on my TV.",
        topics: ["activation", "app"],
        a: [
          "Use the links under “Available on” at the bottom of this page for Roku, Fire TV and Google Play, or search the store on your TV for our name. If it doesn't appear, check your device's software is up to date.",
        ],
      },
    ],
  },
];

const RELATED_MAX = 3;

// FAQ answers tagged with a support category, for the Contact Support form.
export const faqsForTopic = (key) =>
  HELP_SECTIONS.flatMap((s) => s.items)
    .filter((item) => item.topics?.includes(key))
    .slice(0, RELATED_MAX);
